import React from "react";
import clsx from "clsx";

export interface ButtonGroupOption<T = any> {
  value: T;
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
  countBackgroundColor?: string;
}

interface CommonProps<T> {
  options: ButtonGroupOption<T>[];
  className?: string;
  fullWidth?: boolean;
  theme?: "primary" | "secondary";
  backgroundColor?: string;
  size?: "sm" | "md" | "lg";
}

interface SingleSelectProps<T> extends CommonProps<T> {
  multiSelect?: false;
  value?: T;
  onChange: (value: T) => void;
}

interface MultiSelectProps<T> extends CommonProps<T> {
  multiSelect: true;
  value?: T[];
  onChange: (values: T[]) => void;
}

type ButtonGroupProps<T> = SingleSelectProps<T> | MultiSelectProps<T>;

export default function ButtonGroup<T>({
  options,
  value,
  onChange,
  className = "",
  fullWidth = true,
  size = "md",
  backgroundColor = "slate-800",
  multiSelect,
  theme = "primary",
}: ButtonGroupProps<T>) {
  const handleSelect = (optionValue: T) => {
    if (multiSelect) {
      const currentValues = (value as T[]) || [];
      const newSelectedValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      (onChange as (values: T[]) => void)(newSelectedValues);
    } else {
      (onChange as (value: T) => void)(optionValue);
    }
  };

  return (
    <div
      data-testid="button-group"
      className={clsx(
        "isolate inline-flex rounded-xl p-1 gap-1 bg-slate-100 dark:bg-slate-800",
        fullWidth && "w-full",
        className,
      )}
    >
      {options.map((option, index) => {
        const isSelected = multiSelect
          ? Array.isArray(value) && value.includes(option.value)
          : option.value === value;

        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        const positionClasses = isFirst
          ? "rounded-lg"
          : isLast
            ? "rounded-lg "
            : "rounded-lg";

        const activeThemeClasses =
          theme === "primary"
            ? "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-500 z-10"
            : "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:ring-transparent z-10";

        const stateClasses = isSelected
          ? activeThemeClasses
          : "bg-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200";

        const growthClasses = fullWidth ? "flex-1 justify-center" : "";

        const sizeClasses = {
          sm: "px-2 py-1.5 text-xs",
          md: "px-3 py-2 text-sm",
          lg: "px-4 py-2.5 text-base",
        }[size];

        return (
          <button
            key={
              typeof option.value === "object"
                ? JSON.stringify(option.value)
                : String(option.value)
            }
            type="button"
            onClick={() => handleSelect(option.value)}
            data-value={String(option.value)}
            className={`cursor-pointer relative inline-flex items-center justify-center leading-none ${positionClasses} ${stateClasses} ${growthClasses} ${sizeClasses} ${option.icon ? "gap-2" : ""} font-medium focus:z-10 transition-colors`}
          >
            {option.icon}
            {option.label}
            {option.count !== undefined && (
              <span
                data-testid={`button-group-count-${String(option.value)}`}
                style={
                  option.countBackgroundColor
                    ? { backgroundColor: option.countBackgroundColor }
                    : undefined
                }
                className={clsx(
                  // Grid + tabular-nums: centered digit optical alignment (avoids “0” sitting off-centre in rounded pills)
                  "ml-2 grid h-5 min-w-[1.25rem] shrink-0 place-items-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums leading-none",
                  option.countBackgroundColor
                    ? "text-white"
                    : option.count > 0
                      ? "bg-slate-200 text-slate-600 dark:bg-slate-600 dark:text-slate-300"
                      : // Match Badge `color="red"` (Badge.tsx) — soft tint, not solid red-600
                        "bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400",
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
